import React, { useEffect, useMemo, useState } from 'react';
import Select, { ValueType } from 'react-select';
import {
  createInitialTabData,
  fetchGesTabData,
  getStatusStyles,
  GesTabKey,
  QueueType,
  TabData,
  AccessProvisioningStatus,
  GesActionState,
  InspectionModalState,
  TodayActionsProps,
  ReservationCardProps,
  createInitialActionState,
  formatTimestamp,
  getQueuePillClass,
  computeButtonVisibility,
  GesActionType,
  GesActionPayload,
  getGesActionConfig
} from './helper';
import { InspectionModal } from './InspectionModal';
import { EyeIcon, EyeOffIcon } from '../ui/Icons';
import {
  updateGesReservation,
  uploadGesInspectionImages
} from '../ReservationModule/shared/services/data.service';
import { birdToast } from '../shared/components/core-ui/toast';
import { isErrorInfo } from '../shared/interfaces/error-info.interface';

const StatusPill = ({ status }: { status: string }) => {
  return (
    <span
      className={`inline-flex items-center rounded-md border px-2 py-0.5 text-base font-gilroy-medium uppercase tracking-wide md:px-3 md:py-1  ${getStatusStyles(
        status
      )}`}
    >
      {status}
    </span>
  );
};

const QueuePill = ({ type }: { type: QueueType }) => {
  const className = getQueuePillClass(type);

  return (
    <span
      className={`inline-flex items-center rounded-md border px-2 py-0.5 text-base font-gilroy-medium uppercase tracking-wide md:px-3 md:py-1  ${className}`}
    >
      {type}
    </span>
  );
};

const ACCESS_OPTIONS: { value: AccessProvisioningStatus; label: string }[] = [
  { value: 'In-Progress', label: 'In-Progress' },
  { value: 'Done', label: 'Done' },
  { value: 'Pending Option 1', label: 'Pending Option 1' },
  { value: 'Pending Option 2', label: 'Pending Option 2' }
];

const UPCOMING_ACCESS_OPTIONS = ACCESS_OPTIONS.filter(
  (option) => option.value === 'In-Progress' || option.value === 'Done'
);

const TodayActions = ({
  item,
  actionState,
  onAccessChange,
  onCheckIn,
  onCheckOut,
  onInspectionDone,
  onInspectionRejected
}: TodayActionsProps) => {
  const [showActionDetails, setShowActionDetails] = useState(false);

  const isUnitInspected = !!item.unitInspectedAt && !!item.isUnitInspected;

  // Compute button visibility based on inspection state
  const { showCheckIn, showCheckInWithoutInspection, showCheckOut, showInspectionActions } =
    computeButtonVisibility(item, isUnitInspected, actionState.gesCheckInAt);

  const baseButtonClass =
    'rounded-lg border px-4 py-3 text-xl font-gilroy-medium font-semibold  transition-colors disabled:cursor-not-allowed disabled:opacity-50';

  const checkInButton = showCheckIn ? (
    <button
      type="button"
      className={`${baseButtonClass} border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100`}
      onClick={() => onCheckIn(item.id, true)}
    >
      Check In
    </button>
  ) : null;

  const checkInWithoutInspectionButton = showCheckInWithoutInspection ? (
    <button
      type="button"
      className={`${baseButtonClass} border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100`}
      onClick={() => onCheckIn(item.id, false)}
    >
      Check In Without Inspection
    </button>
  ) : null;

  const checkOutButton = showCheckOut ? (
    <button
      type="button"
      className={`${baseButtonClass} border-primary-300 bg-primary-50 text-primary-700 hover:bg-primary-100`}
      onClick={() => onCheckOut(item.id)}
    >
      Check Out
    </button>
  ) : null;

  const inspectionDoneButton = showInspectionActions ? (
    <button
      type="button"
      className={`${baseButtonClass} border-primary-300 bg-primary-50 text-primary-700 hover:bg-primary-100`}
      onClick={() => onInspectionDone(item.id)}
    >
      Inspection Done
    </button>
  ) : null;

  const inspectionRejectedButton = showInspectionActions ? (
    <button
      type="button"
      className={`${baseButtonClass} border-red-300 bg-red-50 text-red-700 hover:bg-red-100`}
      onClick={() => onInspectionRejected(item.id)}
    >
      Inspection Rejected
    </button>
  ) : null;

  const actionButtons = [
    checkInButton,
    checkInWithoutInspectionButton,
    checkOutButton,
    inspectionDoneButton,
    inspectionRejectedButton
  ].filter(Boolean);

  return (
    <div className="mt-3 rounded-xl border border-primary-100 bg-primary-50/40 p-3">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <h4 className="text-lg md:text-2xl font-gilroy-bold uppercase tracking-wide text-neutral-700">
            Actions
          </h4>
          <button
            type="button"
            aria-label={showActionDetails ? 'Hide details' : 'Show details'}
            title={showActionDetails ? 'Hide details' : 'Show details'}
            className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 bg-white text-neutral-600 hover:bg-gray-50"
            onClick={() => setShowActionDetails((prev) => !prev)}
          >
            {showActionDetails ? <EyeOffIcon size="16px" /> : <EyeIcon size="16px" />}
          </button>
        </div>

        <div className="flex items-center gap-3">
          <label className="text-lg mb-0  font-semibold font-gilroy-medium text-neutral-600">
            Access Provisioning
          </label>
          <div className="min-w-[180px] text-sm md:text-base">
            <Select
              value={
                ACCESS_OPTIONS.find(
                  (option) => option.value === actionState.accessProvisioningStatus
                ) || ACCESS_OPTIONS[0]
              }
              options={ACCESS_OPTIONS}
              classNamePrefix="react-select"
              onChange={(
                value: ValueType<{ value: AccessProvisioningStatus; label: string }, false>
              ) => onAccessChange(item.id, value ? value.value : 'In-Progress')}
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">{actionButtons}</div>

      {showActionDetails && (
        <div className="mt-3 grid grid-cols-2 gap-3 text-base md:text-lg text-neutral-600">
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <p className="text-sm md:text-base font-gilroy-semibold text-neutral-500">Inspected</p>
            <p className="text-lg md:text-2xl font-gilroy-semibold text-neutral-700">
              {actionState.isUnitInspected || item.isUnitInspected ? 'Yes' : 'No'}
            </p>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <p className="text-sm md:text-base font-gilroy-semibold text-neutral-500">
              Inspection Date
            </p>
            <p className="text-lg md:text-2xl font-gilroy-semibold text-neutral-700">
              {item.unitInspectedAt
                ? formatTimestamp(item.unitInspectedAt)
                : formatTimestamp(actionState.inspectionDate)}
            </p>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <p className="text-sm md:text-base font-gilroy-semibold text-neutral-500">
              GES Check In
            </p>
            <p className="text-lg md:text-2xl font-gilroy-semibold text-neutral-700">
              {formatTimestamp(actionState.gesCheckInAt)}
            </p>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <p className="text-sm md:text-base font-gilroy-semibold text-neutral-500">
              GES Check Out
            </p>
            <p className="text-lg md:text-2xl font-gilroy-semibold text-neutral-700">
              {formatTimestamp(actionState.gesCheckOutAt)}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

const UpcomingActions = ({
  item,
  actionState,
  onAccessChange
}: {
  item: { id: string };
  actionState: GesActionState;
  onAccessChange: (reservationId: string, status: AccessProvisioningStatus) => void;
}) => {
  return (
    <div className="mt-3 rounded-xl border border-primary-100 bg-primary-50/40 p-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h4 className="text-lg md:text-2xl font-gilroy-bold uppercase tracking-wide text-neutral-700">
          Actions
        </h4>
        <div className="flex items-center gap-3">
          <label className="text-lg mb-0 font-semibold font-gilroy-medium text-neutral-600">
            Access Provisioning
          </label>
          <div className="min-w-[180px] text-sm md:text-base">
            <Select
              value={
                UPCOMING_ACCESS_OPTIONS.find(
                  (option) =>
                    option.value ===
                    (actionState.accessProvisioningStatus === 'Done' ? 'Done' : 'In-Progress')
                ) || UPCOMING_ACCESS_OPTIONS[0]
              }
              options={UPCOMING_ACCESS_OPTIONS}
              classNamePrefix="react-select"
              onChange={(
                value: ValueType<{ value: AccessProvisioningStatus; label: string }, false>
              ) => onAccessChange(item.id, value ? value.value : 'In-Progress')}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const ReservationCard = ({
  item,
  showQueue,
  showTodayActions,
  showUpcomingActions,
  actionState,
  onAccessChange,
  onCheckIn,
  onCheckOut,
  onInspectionDone,
  onInspectionRejected
}: ReservationCardProps) => {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-3.5 shadow-sm md:p-6">
      <div className="mb-3 flex flex-wrap items-start justify-between gap-2 md:mb-5 md:gap-3">
        <h3 className="text-2xl font-gilroy-medium font-semibold text-neutral-700 md:text-3xl">
          {item.guestName} <span>#id:{item.reservationId}</span>
        </h3>

        <div className="flex items-center gap-2">
          {showQueue && <QueuePill type={item.queueType} />}
          <StatusPill status={item.status} />
        </div>
      </div>

      <div className="mb-2 text-base font-gilroy-medium text-neutral-700 md:mb-3 md:text-lg">
        <span className="text-neutral-500">Dates:</span> {item.checkIn} to {item.checkOut}
      </div>

      <div className="grid grid-cols-2 gap-2 text-lg md:gap-3 md:text-xl">
        <div className="rounded-lg bg-gray-50 p-2.5 md:p-3.5">
          <p className="text-neutral-500">Project</p>
          <p className="text-xl font-gilroy-medium text-neutral-700 md:text-2xl">{item.project}</p>
        </div>
        <div className="rounded-lg bg-gray-50 p-2.5 md:p-3.5">
          <p className="text-neutral-500">Arrivals / Departures</p>
          <p className="text-xl font-gilroy-medium text-neutral-700 md:text-2xl">
            {item.queueType}
          </p>
        </div>
        <div className="rounded-lg bg-gray-50 p-2.5 md:p-3.5">
          <p className="text-neutral-500">Area/ Room </p>
          <p className="text-xl font-gilroy-medium text-neutral-700 md:text-2xl">
            {item.area} / {item.roomName}
          </p>
        </div>
        <div className="rounded-lg bg-gray-50 p-2.5 md:p-3.5">
          <p className="text-neutral-500">Phone</p>
          <p className="text-xl font-gilroy-medium text-neutral-700 md:text-2xl">{item.phone}</p>
        </div>
      </div>

      {showTodayActions && (
        <TodayActions
          item={item}
          actionState={actionState}
          onAccessChange={onAccessChange}
          onCheckIn={onCheckIn}
          onCheckOut={onCheckOut}
          onInspectionDone={onInspectionDone}
          onInspectionRejected={onInspectionRejected}
        />
      )}

      {showUpcomingActions && (
        <UpcomingActions item={item} actionState={actionState} onAccessChange={onAccessChange} />
      )}
    </div>
  );
};

export default function GESContainer() {
  const [activeTab, setActiveTab] = useState<GesTabKey>('today');
  const [tabData, setTabData] = useState<TabData>(createInitialTabData());
  const [actionStateByReservation, setActionStateByReservation] = useState<
    Record<string, GesActionState>
  >({});
  const [inspectionModal, setInspectionModal] = useState<InspectionModalState | null>(null);

  const updateReservationActionState = (
    reservationId: string,
    updater: (current: GesActionState) => GesActionState
  ) => {
    setActionStateByReservation((prev) => {
      const current = prev[reservationId] || createInitialActionState();
      return {
        ...prev,
        [reservationId]: updater(current)
      };
    });
  };

  useEffect(() => {
    const fetchTabsData = async () => {
      const data = await fetchGesTabData();
      setTabData(data);

      const reservations = [...data.today.items, ...data.attention.items, ...data.upcoming.items];

      reservations.forEach((reservation) => {
        updateReservationActionState(reservation.id, (current) => ({
          ...current,
          accessProvisioningStatus:
            (reservation.accessProvisioning as AccessProvisioningStatus) ||
            current.accessProvisioningStatus,
          isUnitInspected: reservation.isUnitInspected ?? current.isUnitInspected,
          inspectionDate: reservation.unitInspectedAt ?? current.inspectionDate
        }));
      });
    };

    fetchTabsData();
  }, []);

  const tabs: { key: GesTabKey; label: string; count: number }[] = useMemo(
    () => [
      { key: 'today', label: 'Today (Arrivals & Departures)', count: tabData.today.items.length },
      {
        key: 'attention',
        label: 'Attention Required (Overdue)',
        count: tabData.attention.items.length
      },
      {
        key: 'upcoming',
        label: 'Upcoming Arrivals (30 Days)',
        count: tabData.upcoming.items.length
      }
    ],
    [tabData]
  );

  const activeData = tabData[activeTab];
  const showQueueColumn = activeTab === 'today';
  const showTodayActions = activeTab === 'today';
  const showUpcomingActions = activeTab === 'upcoming';

  const getReservationActionState = (reservationId: string) => {
    return actionStateByReservation[reservationId] || createInitialActionState();
  };

  const emitActionPayload = async (
    reservationId: string,
    action: GesActionType,
    payload: Record<string, any>
  ) => {
    const { gesPayload, loadingMessage, successMessage } = getGesActionConfig(action, payload);

    const hasPayload = Object.keys(gesPayload).length > 0;
    if (!hasPayload) return;

    const promise = updateGesReservation(reservationId, gesPayload as any).then((result) => {
      if (isErrorInfo(result)) {
        throw result;
      }
      return result;
    });

    birdToast.promise(promise, {
      loadingMessage,
      successMessage,
      errorFailedMessage: 'Failed to update reservation'
    });
  };

  const handleAccessChange = (reservationId: string, status: AccessProvisioningStatus) => {
    updateReservationActionState(reservationId, (current) => ({
      ...current,
      accessProvisioningStatus: status
    }));

    emitActionPayload(reservationId, 'access_provisioning', {
      accessProvisioningStatus: status
    });
  };

  const handleCheckIn = (reservationId: string, withInspection: boolean) => {
    const now = Date.now();

    updateReservationActionState(reservationId, (current) => ({
      ...current,
      gesCheckInAt: now,
      gesCheckOutAt: null,
      isUnitInspected: withInspection ? current.isUnitInspected : false
    }));

    emitActionPayload(reservationId, withInspection ? 'check_in' : 'check_in_without_inspection', {
      actualCheckInAt: now,
      withInspection
    });
  };

  const handleCheckOut = (reservationId: string) => {
    const now = Date.now();

    updateReservationActionState(reservationId, (current) => ({
      ...current,
      gesCheckOutAt: now
    }));

    emitActionPayload(reservationId, 'check_out', {
      actualCheckOutAt: now
    });
  };

  const handleOpenInspectionDone = (reservationId: string) => {
    setInspectionModal({
      reservationId,
      amenitiesPhoto: null,
      bedPhoto: null,
      toiletPhoto: null,
      livingAreaPhoto: null
    });
  };

  const handleInspectionRejected = (reservationId: string) => {
    updateReservationActionState(reservationId, (current) => ({
      ...current,
      isUnitInspected: false,
      isUnitCleaned: false,
      inspectionDate: Date.now()
    }));

    emitActionPayload(reservationId, 'inspection_rejected', {
      isUnitInspected: false,
      isUnitCleaned: false,
      inspectionDate: Date.now()
    });
  };

  const handleInspectionPhotoChange = (
    photoType: keyof Omit<InspectionModalState, 'reservationId'>,
    file: File | null
  ) => {
    setInspectionModal((prev) =>
      prev
        ? {
            ...prev,
            [photoType]: file
          }
        : prev
    );
  };

  const handleSubmitInspectionDone = () => {
    if (!inspectionModal) return;

    const { reservationId, amenitiesPhoto, bedPhoto, toiletPhoto, livingAreaPhoto } =
      inspectionModal;
    if (!amenitiesPhoto || !bedPhoto || !toiletPhoto || !livingAreaPhoto) return;

    const now = Date.now();

    const promise = uploadGesInspectionImages(reservationId, {
      amenitiesPhoto,
      bedPhoto,
      toiletPhoto,
      livingAreaPhoto
    }).then((uploadResult) => {
      if (isErrorInfo(uploadResult)) {
        throw uploadResult;
      }

      updateReservationActionState(reservationId, (current) => ({
        ...current,
        isUnitInspected: true,
        isUnitCleaned: true,
        inspectionDate: now
      }));

      return updateGesReservation(reservationId, {
        isUnitInspected: true,
        isUnitCleaned: true
      }).then(async (result) => {
        if (isErrorInfo(result)) {
          throw result;
        }

        const data = await fetchGesTabData();
        setTabData(data);
        setInspectionModal(null);
        return result;
      });
    });

    birdToast.promise(promise, {
      loadingMessage: 'Uploading inspection images...',
      successMessage: 'Inspection completed successfully',
      errorFailedMessage: 'Failed to complete inspection'
    });
  };

  return (
    <div className="min-h-screen bg-creams-50 px-3 py-3 md:px-6 md:py-4">
      <div className="mb-4 flex flex-wrap items-center gap-3 md:mb-5 md:gap-4">
        <h1 className="hidden text-2xl font-recoleta font-semibold text-neutral-500 md:block md:text-3xl">
          Guest Experience Services
        </h1>

        <div className="flex flex-1 flex-wrap items-center justify-center gap-2">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              className={`inline-flex items-center rounded-md border px-3 py-2 text-sm font-gilroy-medium transition-colors md:px-4 md:py-2.5 md:text-lg ${
                activeTab === tab.key
                  ? 'border-primary-300 bg-primary-50 text-primary-700'
                  : 'border-gray-200 bg-white text-neutral-600 hover:bg-gray-50'
              }`}
            >
              {tab.label}
              <span className="ml-2 inline-flex size-6 items-center justify-center rounded-full bg-white/80 px-1.5 text-sm font-semibold font-gilroy-medium md:size-7 md:text-base">
                {tab.count}
              </span>
            </button>
          ))}
        </div>
      </div>

      {activeData.loading ? (
        <div className="rounded-2xl border border-gray-200 bg-white px-4 py-8 text-center text-lg text-neutral-500 shadow-sm md:py-10 md:text-2xl">
          Loading reservations...
        </div>
      ) : activeData.error ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-8 text-center text-lg text-red-700 shadow-sm md:py-10 md:text-2xl">
          {activeData.error}
        </div>
      ) : activeData.items.length === 0 ? (
        <div className="rounded-2xl border border-gray-200 bg-white px-4 py-8 text-center text-lg text-neutral-500 shadow-sm md:py-10 md:text-2xl">
          No reservations in this tab.
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 md:gap-4 2xl:grid-cols-3">
          {activeData.items.map((item, index) => (
            <ReservationCard
              key={`${item.id}-${item.queueType}-${index}`}
              item={item}
              showQueue={showQueueColumn}
              showTodayActions={showTodayActions}
              showUpcomingActions={showUpcomingActions}
              actionState={getReservationActionState(item.id)}
              onAccessChange={handleAccessChange}
              onCheckIn={handleCheckIn}
              onCheckOut={handleCheckOut}
              onInspectionDone={handleOpenInspectionDone}
              onInspectionRejected={handleInspectionRejected}
            />
          ))}
        </div>
      )}

      {inspectionModal && (
        <InspectionModal
          inspectionModal={inspectionModal}
          onPhotoChange={handleInspectionPhotoChange}
          onSubmit={handleSubmitInspectionDone}
          onCancel={() => setInspectionModal(null)}
        />
      )}
    </div>
  );
}
